'use client';

import { useMergeRefs } from '@floating-ui/react';
import { type MouseEventHandler, forwardRef, useCallback, useRef } from 'react';

import { css, cx } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { useInteractiveElementKeyDownCallback } from '../../hooks/use-interactive-element-key-down-callback';
import { toggleCardRootRecipe } from './styles/toggle-card-root.styles';
import type { ToggleCardRootPropsInterface } from './types/toggle-card-root.types';

/**
 * @since 10.0.0
 */
export const ToggleCardRoot = forwardRef<HTMLDivElement, ToggleCardRootPropsInterface>(
  (props: ToggleCardRootPropsInterface, forwardedRef) => {
    const {
      selected,
      defaultSelected,
      onSelectedChange,
      outlined = true,
      className,
      css: cssProp,
      disabled = false,
      elevated = true,
      tone = 'neutral',
      onClick,
      onKeyDown,
      tabIndex = 0,
      ...rest
    } = props;

    const [selectedState, setSelectedState] = useControllableValue<boolean | undefined>({
      controlledValue: selected,
      uncontrolledValue: defaultSelected,
      isControlled: 'selected' in props,
      isUncontrolled: 'defaultSelected' in props,
      onChange: (newSelected) => {
        if (newSelected !== undefined) {
          onSelectedChange?.(newSelected);
        }
      },
      componentName: 'ToggleCard.Root',
      controlledPropName: 'selected',
      uncontrolledPropName: 'defaultSelected',
    });

    const handleClick: MouseEventHandler<HTMLDivElement> = useCallback(
      (e) => {
        if (!disabled) {
          onClick?.(e);
          setSelectedState((prev) => !prev);
        }
      },
      [setSelectedState, disabled, onClick],
    );

    const toggleCardRef = useRef<HTMLDivElement>(null);
    const refs = useMergeRefs([toggleCardRef, forwardedRef]);

    const onKeyDownCallback = useInteractiveElementKeyDownCallback({
      ref: toggleCardRef,
      onClick: handleClick,
      onKeyDown,
    });

    const styles = css(
      toggleCardRootRecipe.raw({ outlined, disabled, elevated, tone, selected: !!selectedState }),
      cssProp,
    );

    // Since toggle card elements don't support the `disabled` attribute,
    // we need to override any event interactions. A disabled toggle card is
    // also removed from the tab order (`focusable: false`).
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    return (
      <div
        ref={refs}
        role="button"
        onClick={handleClick}
        onKeyDown={onKeyDownCallback}
        data-c11n-component="ToggleCard.Root"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_toggle-card__root', styles, className)}
        aria-pressed={!!selectedState}
        {...rest}
        {...(disabled ? disabledInteractionsProps : { tabIndex })}
      />
    );
  },
);

ToggleCardRoot.displayName = 'ToggleCard.Root';
